import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import MUIDataTable from 'mui-datatables';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import GroupIcon from '@material-ui/icons/Group';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';

import { sortRows } from '../../../components/tableSort';
import { contactsLabel } from '../../../constants/whatsapp';
import { listStats } from '../../../constants/whatsapp-contacts';
import { actions as mainActions } from '../../Root/actions';
import { actions as whatsappActions } from '../actions';
import ConfirmDialog from '../components/ConfirmDialog';
import ConsentChip from '../components/ConsentChip';
import ConsentDialog from '../components/ConsentDialog';
import ConsentQueue from '../components/ConsentQueue';
import ContactFilters from '../components/ContactFilters';
import WithoutPhoneHint from '../components/WithoutPhoneHint';
import { NAME as WHATSAPP_NAME } from '../reducer';
import { colors } from '../theme';
import { tableTextLabels } from '../../../components/tableTextLabels';
import { matchesSearch } from '../../../components/searchMatch';

function mapStateToProps(state) {
  return {
    ...state[WHATSAPP_NAME],
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...whatsappActions, ...mainActions }, dispatch);

const useStyles = makeStyles((theme) => ({
  consentListChip: { marginBottom: 12 },
  card: {
    padding: theme.spacing(2.5),
    borderRadius: 10,
  },
  panelTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  panelTitleIcon: {
    fontSize: '20px',
    marginRight: theme.spacing(1),
  },
  activeItem: {
    backgroundColor: colors.accentSoft,
  },
  dirtyMark: {
    color: colors.warnText,
    fontWeight: 600,
  },
  closeList: {
    textTransform: 'none',
    marginLeft: theme.spacing(1.5),
  },
  tabs: {
    borderBottom: `1px solid ${colors.border}`,
    marginBottom: theme.spacing(2),
  },
  // Панель действия закреплена: название, счётчики и кнопка сохранения
  // должны быть видны и на пятой странице таблицы.
  actionBar: {
    position: 'sticky',
    top: 0,
    // Ячейка с чекбоксом у mui-datatables остаётся sticky с z-index 110 даже
    // при fixedHeader: false, поэтому панель должна быть выше — иначе
    // чекбоксы просвечивают поверх неё.
    zIndex: 200,
    backgroundColor: colors.panel,
    borderBottom: `1px solid ${colors.border}`,
    boxShadow: '0 4px 8px -6px rgba(0, 0, 0, 0.35)',
    padding: theme.spacing(1.5, 0),
    marginBottom: theme.spacing(2),
  },
  searchField: {
    flexGrow: 1,
    minWidth: 230,
    marginRight: theme.spacing(2),
  },
  found: {
    color: colors.textDim,
    fontSize: '13px',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(2),
  },
  barAction: {
    textTransform: 'none',
    marginRight: theme.spacing(2),
    whiteSpace: 'nowrap',
  },
  actionMode: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    color: colors.textDim,
    fontSize: '11px',
    letterSpacing: '1.1px',
    textTransform: 'uppercase',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  nameRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2.5),
  },
  nameField: {
    flexGrow: 1,
    minWidth: 240,
    marginRight: theme.spacing(2),
  },
  counters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  chip: {
    marginRight: theme.spacing(1),
  },
  chipOk: {
    backgroundColor: colors.okBg,
    color: colors.okText,
    marginRight: theme.spacing(1),
  },
  chipWarn: {
    backgroundColor: colors.warnBg,
    color: colors.warnText,
    marginRight: theme.spacing(1),
  },
  saveButton: {
    textTransform: 'none',
    marginRight: theme.spacing(1),
  },
  hint: {
    display: 'block',
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
}));

const sameIds = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }
  const set = {};
  a.forEach((id) => {
    set[id] = true;
  });
  return b.every((id) => set[id]);
};

function BroadcastLists(props) {
  const classes = useStyles();
  const { contacts, lists } = props;

  useEffect(() => {
    props.fetchBroadcastLists();
    props.fetchCompanies();
    props.fetchConsents();
    // Клиенты отвечают на приветствие в любой момент: статусы обновляются сами.
    const timer = setInterval(() => props.fetchConsents(), 20000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Контакт, чью карточку «Информация о подтверждении» сейчас открыли.
  const [infoId, setInfoId] = useState(null);

  // С плашки «Без согласия» на странице рассылки приходят сразу на вкладку «Нужно собрать согласие»
  // и видят только контакты того списка, с которого пришли (list=…); фильтр снимается крестиком.
  const hashParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.hash.split('?')[1] || '' : ''
  );
  const [tab, setTab] = useState(() => (hashParams.get('tab') === 'consent' ? 1 : 0));
  const [consentListId, setConsentListId] = useState(() =>
    hashParams.get('tab') === 'consent' ? hashParams.get('list') : null
  );
  const [editedId, setEditedId] = useState(null);
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [nameError, setNameError] = useState('');

  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [region, setRegion] = useState('');
  const [consent, setConsent] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  // { kind: 'delete' | 'discard', list?, next? }
  const [confirm, setConfirm] = useState(null);

  const selectedSet = useMemo(() => {
    const map = {};
    selectedIds.forEach((id) => {
      map[id] = true;
    });
    return map;
  }, [selectedIds]);

  const types = useMemo(() => {
    const seen = {};
    contacts.forEach((c) => {
      seen[c.type] = true;
    });
    return Object.keys(seen).sort();
  }, [contacts]);

  const regions = useMemo(() => {
    const seen = {};
    contacts.forEach((c) => {
      seen[c.region] = true;
    });
    return Object.keys(seen).sort();
  }, [contacts]);

  // Согласие ещё можно получить: клиента не спрашивали или он не ответил. Отказавшихся
  // здесь нет: им писать нельзя.
  const pendingConsent = useMemo(
    () =>
      contacts.filter(
        (contact) =>
          contact.consentStatus === 'none' || contact.consentStatus === 'pending'
      ),
    [contacts]
  );

  const consentList = consentListId
    ? lists.find((item) => String(item.id) === String(consentListId))
    : null;
  const consentPending = useMemo(() => {
    if (!consentList) return pendingConsent;
    const ids = new Set(consentList.contactIds);
    return pendingConsent.filter((contact) => ids.has(contact.id));
  }, [consentList, pendingConsent]);

  const contactByPhone = useMemo(() => {
    const map = {};
    contacts.forEach((contact) => {
      map[contact.phone] = contact;
    });
    return map;
  }, [contacts]);

  const withoutProof = useMemo(
    () =>
      contacts.filter((contact) => contact.optIn && !contact.consentEvidence)
        .length,
    [contacts]
  );

  // Контакты, показанные в таблице: либо результат фильтров, либо только
  // отмеченные — чтобы менеджер мог проверить, кого он набрал.
  const visible = useMemo(() => {
    if (showSelectedOnly) {
      return contacts.filter((contact) => selectedSet[contact.id]);
    }
    const query = search.trim().toLowerCase();
    return contacts.filter((contact) => {
      if (type && contact.type !== type) {
        return false;
      }
      if (region && contact.region !== region) {
        return false;
      }
      if (consent === 'nodoc') {
        if (!contact.optIn || contact.consentEvidence) {
          return false;
        }
      } else if (consent && contact.consentStatus !== consent) {
        return false;
      }
      if (query) {
        // Телефон ищется по цифрам: с пробелами и без.
        if (!matchesSearch(query, [contact.name, contact.company], [contact.phone])) {
          return false;
        }
      }
      return true;
    });
  }, [contacts, selectedSet, showSelectedOnly, search, type, region, consent]);

  const selectedIndices = useMemo(() => {
    const indices = [];
    visible.forEach((contact, index) => {
      if (selectedSet[contact.id]) {
        indices.push(index);
      }
    });
    return indices;
  }, [visible, selectedSet]);

  const selectedOptIn = useMemo(
    () =>
      contacts.filter((contact) => selectedSet[contact.id] && contact.optIn)
        .length,
    [contacts, selectedSet]
  );

  const withoutConsent = selectedIds.length - selectedOptIn;

  const baseline = useMemo(() => {
    if (!editedId) {
      return { name: '', ids: [] };
    }
    const list = lists.find((item) => item.id === editedId);
    return list ? { name: list.name, ids: list.contactIds } : { name: '', ids: [] };
  }, [editedId, lists]);

  const dirty =
    name !== baseline.name || !sameIds(selectedIds, baseline.ids);

  // Что осталось сделать, чтобы список сохранился. Без этой подсказки было
  // непонятно, почему кнопка не срабатывает.
  let guidance = '';
  if (selectedIds.length === 0) {
    guidance = 'Отметьте контакты галочками в таблице ниже';
  } else if (!name.trim()) {
    guidance = 'Осталось ввести название';
  }

  const editedList = lists.find((item) => item.id === editedId);

  const tableData = useMemo(
    () =>
      visible.map((contact) => ({
        name: contact.name,
        company: contact.company,
        phone: contact.phone,
        type: contact.type,
        region: contact.region,
        status: contact.consentStatus,
      })),
    // selectedSet в зависимостях: новый массив заставляет таблицу заново расставить строки,
    // и отмеченный контакт сразу уходит в начало.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, selectedSet]
  );

  const columns = [
    { name: 'name', label: 'Директор' },
    { name: 'company', label: 'Компания' },
    {
      name: 'phone',
      label: 'Телефон',
      options: {
        // Иначе номер разрывается на две строки.
        setCellProps: () => ({ style: { whiteSpace: 'nowrap' } }),
      },
    },
    { name: 'type', label: 'Тип компании' },
    { name: 'region', label: 'Область' },
    {
      name: 'status',
      label: 'Согласие',
      options: {
        // Телефон уникален, поэтому по нему безопасно достать контакт:
        // индекс строки в mui-datatables меняется при сортировке.
        customBodyRender: (value, tableMeta) => {
          const contact = contactByPhone[tableMeta.rowData[2]];
          return (
            <Tooltip title="Информация о подтверждении">
              <span>
                <ConsentChip
                  status={value}
                  onClick={() => contact && setInfoId(contact.id)}
                />
              </span>
            </Tooltip>
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    search: false,
    // Свой залипающий заголовок mui-datatables отключён: он липнет к тому же
    // top: 0, что и панель действия, и с более высоким z-index рисовался
    // поверх неё. Липнуть должно что-то одно.
    fixedHeader: false,
    download: false,
    print: false,
    viewColumns: false,
    selectableRows: 'multiple',
    selectableRowsHeader: true,
    rowsSelected: selectedIndices,
    // Отметки контактов, скрытых текущим фильтром, сохраняются: иначе
    // выбор терялся бы при каждом изменении фильтра.
    onRowSelectionChange: (current, allSelected) => {
      const visibleMap = {};
      visible.forEach((contact) => {
        visibleMap[contact.id] = true;
      });
      const nowSelected = allSelected
        .map((row) => visible[row.dataIndex])
        .filter(Boolean)
        .map((contact) => contact.id);
      const keptHidden = selectedIds.filter((id) => !visibleMap[id]);
      setSelectedIds(keptHidden.concat(nowSelected));
    },
    customToolbarSelect: () => null,
    rowsPerPage: 25,
    rowsPerPageOptions: [25, 50, 100],
    sortOrder: { name: 'name', direction: 'asc' },
    // Сначала отмеченные, внутри каждой группы обычная сортировка колонки, чтобы выбранных не
    // искать по страницам. Контакт берём по индексу строки в данных (row.index), а не по телефону:
    // один номер бывает у нескольких компаний, и по телефону отметка доставалась чужой строке.
    customSort: (rows, colIndex, order) => {
      const first = [];
      const rest = [];
      sortRows(rows, colIndex, order).forEach((row) => {
        const contact = visible[row.index];
        (contact && selectedSet[contact.id] ? first : rest).push(row);
      });
      return first.concat(rest);
    },
    textLabels: tableTextLabels,
  };

  const resetFilters = () => {
    setSearch('');
    setType('');
    setRegion('');
    setConsent('');
  };

  const loadList = (list) => {
    setEditedId(list ? list.id : null);
    setName(list ? list.name : '');
    setSelectedIds(list ? list.contactIds : []);
    setNameError('');
    setShowSelectedOnly(false);
  };

  // «Изменить список» на странице рассылки открывает этот список сразу (edit=…), когда списки загружены.
  const [editFromHash, setEditFromHash] = useState(() => hashParams.get('edit'));
  // Параметры из адреса нужны один раз: адрес чистим, чтобы перезагрузка страницы не открывала
  // снова тот же список или фильтр.
  useEffect(() => {
    if (window.location.hash.indexOf('?') !== -1 && window.history && window.history.replaceState) {
      window.history.replaceState(window.history.state, '', window.location.hash.split('?')[0]);
    }
  }, []);
  useEffect(() => {
    if (!editFromHash) return;
    const list = lists.find((item) => String(item.id) === String(editFromHash));
    if (list) {
      loadList(list);
      setEditFromHash(null);
    }
  }, [editFromHash, lists]); // eslint-disable-line react-hooks/exhaustive-deps

  // Пока набор не сохранён, переключение списка его затрёт — спрашиваем.
  const guarded = (action) => {
    if (dirty) {
      setConfirm({ kind: 'discard', run: action });
      return;
    }
    action();
  };

  const handleSelectAllFound = () => {
    const merged = new Set(selectedIds);
    visible.forEach((contact) => {
      merged.add(contact.id);
    });
    setSelectedIds(Array.from(merged));
  };

  const handleMarkConsent = async (contactIds, source, evidence) => {
    const result = await props.markConsent(contactIds, 'confirmed', source, evidence);
    if (!result.ok) {
      props.showAlert({
        show: true,
        alertTitle: 'Согласие не сохранено',
        alertText: result.error,
        severity: 'error',
        style: {},
        variant: 'outlined',
      });
      return false;
    }
    const left = result.leftDeclined || 0;
    props.showAlert({
      show: true,
      alertTitle: `Согласие отмечено: ${contactsLabel(contactIds.length - left)}`,
      alertText:
        (evidence
          ? `Основание: ${source} · ${evidence}`
          : `Основание: ${source}. Артефакт не указан — журнал без документа не доказательство.`) +
        (left > 0
          ? ` Отказавшим клиентам (${left}) отметка не поставлена: отказ снимает только сам клиент.`
          : ''),
      severity: evidence && left === 0 ? 'success' : 'warning',
      style: {},
      variant: 'outlined',
    });
    return true;
  };

  const infoContact = contacts.find((contact) => contact.id === infoId) || null;

  const handleSave = async () => {
    if (selectedIds.length === 0) {
      props.showAlert({
        show: true,
        alertTitle: 'Сначала отметьте контакты',
        alertText:
          'Отметьте нужные контакты галочками в таблице — можно отфильтровать базу и нажать «Отметить всё найденное».',
        severity: 'warning',
        style: {},
        variant: 'outlined',
      });
      return;
    }
    if (!name.trim()) {
      setNameError('Введите название списка.');
      return;
    }
    if (!editedId) {
      setConfirm({ kind: 'create' });
      return;
    }
    await saveList();
  };

  const saveList = async () => {
    const saved = await props.saveBroadcastList({
      id: editedId,
      name: name.trim(),
      contactIds: selectedIds,
    });
    if (saved && !saved.error) {
      setEditedId(saved.id);
      props.showAlert({
        show: true,
        alertTitle: editedId
          ? `Список «${name.trim()}» обновлён`
          : `Список «${name.trim()}» создан`,
        alertText: `Контактов: ${selectedIds.length} · получат сообщение: ${selectedOptIn}`,
        severity: 'success',
        style: {},
        variant: 'outlined',
      });
    } else {
      props.showAlert({
        show: true,
        alertTitle: 'Список не сохранён',
        alertText: (saved && saved.error) || 'Не удалось сохранить список. Попробуйте ещё раз.',
        severity: 'error',
        style: {},
        variant: 'outlined',
      });
    }
  };

  const handleDelete = (list) => {
    props.deleteBroadcastList(list.id);
    if (editedId === list.id) {
      loadList(null);
    }
    props.showAlert({
      show: true,
      alertTitle: `Список «${list.name}» удалён`,
      severity: 'info',
      style: {},
      variant: 'outlined',
    });
  };

  const confirmProps = () => {
    if (!confirm) {
      return { open: false, title: '', text: '', confirmLabel: '' };
    }
    if (confirm.kind === 'create') {
      return {
        open: true,
        title: `Создать список «${name.trim()}»?`,
        text:
          `Контактов: ${selectedIds.length} · получат сообщение: ${selectedOptIn}` +
          (withoutConsent > 0
            ? ` · без согласия: ${withoutConsent} (им рассылка не уйдёт).`
            : '.'),
        confirmLabel: 'Создать',
      };
    }
    if (confirm.kind === 'delete') {
      return {
        open: true,
        title: 'Удалить список?',
        text: `Список «${confirm.list.name}» будет удалён. Контакты останутся в базе.`,
        confirmLabel: 'Удалить',
      };
    }
    return {
      open: true,
      title: 'Набор не сохранён',
      text: 'Изменения в текущем списке потеряются. Продолжить?',
      confirmLabel: 'Продолжить',
    };
  };

  const runConfirm = () => {
    const { kind } = confirm;
    setConfirm(null);
    if (kind === 'create') {
      saveList();
    } else if (kind === 'delete') {
      handleDelete(confirm.list);
    } else {
      confirm.run();
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Paper className={classes.card}>
          <div className={classes.panelTitle}>
            <GroupIcon className={classes.panelTitleIcon} />
            <Typography variant="subtitle2">Сохранённые списки</Typography>
          </div>
          <List disablePadding>
            {/* Всегда первый и без корзины: это вся база, из которой набирается новый
                список. Пока открыт сохранённый список, нажатие возвращает к чистой форме. */}
            <ListItem
              button
              divider
              selected={editedId === null}
              className={editedId === null ? classes.activeItem : ''}
              onClick={() => guarded(() => loadList(null))}
            >
              <ListItemText
                primary={
                  <span>
                    Новый список
                    {editedId === null && dirty && (
                      <span className={classes.dirtyMark}> · не сохранён</span>
                    )}
                  </span>
                }
                secondary={`Вся база · ${contactsLabel(contacts.length)}`}
              />
              <ListItemSecondaryAction>
                <AddIcon fontSize="small" color="action" />
              </ListItemSecondaryAction>
            </ListItem>
            {lists.map((list) => {
              const stats = listStats(list, contacts);
              const isOpen = editedId === list.id;
              return (
                <ListItem
                  key={list.id}
                  button
                  divider
                  selected={isOpen}
                  className={isOpen ? classes.activeItem : ''}
                  onClick={() => guarded(() => loadList(list))}
                >
                  <ListItemText
                    primary={
                      <span>
                        {list.name}
                        {isOpen && dirty && (
                          <span className={classes.dirtyMark}> · изменён</span>
                        )}
                      </span>
                    }
                    secondary={`${contactsLabel(stats.total)} · получат ${
                      stats.optIn
                    }`}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Удалить список">
                      <IconButton
                        size="small"
                        onClick={() => setConfirm({ kind: 'delete', list })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Grid>

      <Grid item xs={12} md={9}>
        <Paper className={classes.card}>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            className={classes.tabs}
            onChange={(event, value) => setTab(value)}
          >
            <Tab label={`База контактов (${contacts.length})`} />
            <Tab label={`Нужно собрать согласие (${consentPending.length})`} />
          </Tabs>

          {tab === 1 ? (
            <>
              {consentList && (
                <Chip
                  className={classes.consentListChip}
                  label={`Только список «${consentList.name}»`}
                  onDelete={() => setConsentListId(null)}
                />
              )}
              <ConsentQueue
                contacts={consentPending}
                onMarkConsent={handleMarkConsent}
                onShowInfo={setInfoId}
              />
            </>
          ) : (
            <>
              {(props.contactsError || (props.withoutPhoneCompanies || []).length > 0) && (
                <Typography
                  variant="caption"
                  className={classes.hint}
                  style={{ color: props.contactsError ? '#d32f2f' : undefined, marginBottom: 12 }}
                >
                  {props.contactsError
                    ? `Не удалось загрузить клиентов из основной системы: ${props.contactsError}. `
                    : ''}
                  <WithoutPhoneHint companies={props.withoutPhoneCompanies} />
                </Typography>
              )}
              <div className={classes.actionBar}>
                <div className={classes.actionRow}>
                  <TextField
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    placeholder="Поиск: имя, компания, телефон"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="disabled" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography className={classes.found}>
                    {showSelectedOnly
                      ? `Выбранные: ${visible.length}`
                      : `Найдено: ${visible.length}`}
                  </Typography>
                  <Button
                    className={classes.barAction}
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<DoneAllIcon />}
                    disabled={visible.length === 0 || showSelectedOnly}
                    onClick={handleSelectAllFound}
                  >
                    Отметить всё найденное
                  </Button>
                  <div className={classes.counters}>
                    <Chip
                      className={classes.chip}
                      variant="outlined"
                      label={`Выбрано: ${selectedIds.length}`}
                    />
                    <Chip
                      className={classes.chipOk}
                      label={`Получат: ${selectedOptIn}`}
                    />
                    {withoutConsent > 0 && (
                      <Chip
                        className={classes.chipWarn}
                        label={`Без согласия: ${withoutConsent}`}
                      />
                    )}
                  </div>
                  {/* Для нового списка кнопка всегда активна: серая кнопка
                      не объясняет, что делать дальше — валидация объясняет. */}
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    className={classes.saveButton}
                    disabled={editedId ? !dirty : false}
                    onClick={handleSave}
                  >
                    {editedId ? 'Сохранить' : 'Создать список'}
                  </Button>
                </div>
              </div>

              <Typography className={classes.actionMode}>
                {editedList
                  ? `Редактирование: ${editedList.name}`
                  : 'Новый список'}
                {editedList && (
                  <Button
                    size="small"
                    className={classes.closeList}
                    onClick={() => guarded(() => loadList(null))}
                  >
                    Закрыть и создать новый
                  </Button>
                )}
              </Typography>
              <div className={classes.nameRow}>
                <TextField
                  className={classes.nameField}
                  variant="outlined"
                  size="small"
                  label="Название списка"
                  placeholder="Соя — покупатели"
                  value={name}
                  error={Boolean(nameError)}
                  helperText={nameError || guidance}
                  onChange={(event) => {
                    setName(event.target.value);
                    setNameError('');
                  }}
                />
                {dirty && (
                  <Button
                    className={classes.saveButton}
                    onClick={() => loadList(editedList ? editedList : null)}
                  >
                    Отменить изменения
                  </Button>
                )}
              </div>

              <ContactFilters
                hasSearch={search !== ''}
                type={type}
                onTypeChange={setType}
                region={region}
                onRegionChange={setRegion}
                consent={consent}
                onConsentChange={setConsent}
                types={types}
                regions={regions}
                selectedCount={selectedIds.length}
                showSelectedOnly={showSelectedOnly}
                onToggleSelectedOnly={() =>
                  setShowSelectedOnly(!showSelectedOnly)
                }
                onReset={resetFilters}
              />

              <MUIDataTable
                title=""
                data={tableData}
                columns={columns}
                options={options}
              />

              {withoutProof > 0 && (
                <Typography variant="caption" className={classes.hint}>
                  У {withoutProof} из {contacts.length} контактов согласие
                  отмечено, но документ не указан. Чтобы увидеть их, выберите
                  в фильтре «Согласие» → «Подтверждён, но без документа».
                </Typography>
              )}
            </>
          )}
        </Paper>
      </Grid>

      <ConsentDialog
        contact={infoContact}
        onClose={() => setInfoId(null)}
        onMark={(contactId, status, source, evidence) =>
          props.markConsent([contactId], status, source, evidence)
        }
      />
      <ConfirmDialog
        {...confirmProps()}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
      />
    </Grid>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(BroadcastLists);
